/**
 * Where the link in our reset email lands.
 *
 * Firebase hosts a page that does this, but it lives on a
 * `<project>.firebaseapp.com` address with the project's name on it — not
 * somewhere a customer of Kono expects to type a new password. The code in the
 * URL is Firebase's own, so it is still Firebase that checks it and sets the
 * password; only the page around it is ours.
 */
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import { firebaseAuth } from "../../../firebase";
import { useEffect } from "react";

/** What a rejected code or password means, in the customer's terms. */
export const resetErrorMessage = (code: string): string => {
  switch (code) {
    case "auth/expired-action-code":
      return "That link has expired. Ask for a new one and use it within the hour.";
    case "auth/invalid-action-code":
      return "That link has already been used, or it was cut short by your email program. Ask for a new one.";
    case "auth/user-disabled":
      return "That account is closed. Contact support@kono.ai and we will help.";
    case "auth/user-not-found":
      return "That account no longer exists. Contact support@kono.ai and we will help.";
    case "auth/weak-password":
      return "Choose a longer password — at least eight characters.";
    default:
      return "The password could not be changed. Ask for a new link and try again.";
  }
};

export default function ResetPasswordPage({ logoImg }: { logoImg: string }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // Our own email sends `code`; a link from Firebase's template would carry
  // `oobCode`, so both are accepted and neither needs explaining to anyone.
  const code = params.get("code") || params.get("oobCode") || "";
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState(code ? "" : resetErrorMessage("auth/invalid-action-code"));
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!code) {
      setChecking(false);
      return;
    }
    // Checking before showing the form means a dead link says so up front,
    // rather than after someone has chosen and typed a password twice.
    verifyPasswordResetCode(firebaseAuth, code)
      .then((address) => setEmail(address))
      .catch((e) => setError(resetErrorMessage(e?.code || "")))
      .finally(() => setChecking(false));
  }, [code]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (password.length < 8) {
      setError("Choose a password of at least eight characters.");
      return;
    }
    if (password !== confirmation) {
      setError("Those two passwords are not the same.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await confirmPasswordReset(firebaseAuth, code, password);
      setDone(true);
      // Long enough to read what happened, short enough not to feel stuck.
      setTimeout(() => navigate("/signin"), 2500);
    } catch (e: any) {
      setError(resetErrorMessage(e?.code || ""));
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    "mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none";

  return (
    <div className="mx-auto md:w-[30em] px-4 py-10">
      <Link to="/">
        <img src={logoImg} alt="Kono" className="mx-auto mb-10 h-[4em] w-auto" />
      </Link>
      <h1 className="mb-5 text-center text-xl font-semibold">
        Choose a new password
      </h1>

      {checking && (
        <p className="text-center text-sm text-gray-600">Checking your link…</p>
      )}

      {!checking && done && (
        <div className="rounded border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-800">
          Your password is changed. Taking you to the sign-in page…
        </div>
      )}

      {!checking && !done && !!email && (
        <form onSubmit={submit}>
          <p className="mb-4 text-sm text-gray-600">
            Setting a new password for{" "}
            <span className="font-medium text-gray-900">{email}</span>.
          </p>
          <label className="block text-sm">
            <span className="text-gray-900">New password</span>
            <input
              type="password"
              name="password"
              autoComplete="new-password"
              className={inputClass}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          <label className="mt-3 block text-sm">
            <span className="text-gray-900">New password again</span>
            <input
              type="password"
              name="confirmation"
              autoComplete="new-password"
              className={inputClass}
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
            />
          </label>
          <button
            type="submit"
            disabled={saving}
            className="mt-5 w-full rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Change my password"}
          </button>
        </form>
      )}

      {!checking && !done && !!error && (
        <div className="mt-4 rounded border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {!checking && !done && (
        <div className="mt-6 text-center text-sm">
          <Link to="/forgot-password" className="text-blue-700 hover:underline">
            Send me a new link
          </Link>
          <span className="text-gray-400"> · </span>
          <Link to="/signin" className="text-blue-700 hover:underline">
            Sign in
          </Link>
        </div>
      )}
    </div>
  );
}
